import { useFormContext } from "react-hook-form";
import styled from "styled-components";

/**
 * CLE-2300 — inline rule description + worked-math examples for the Package
 * ruleset's trigger / bundled lists. Mirrors the visual pattern of
 * `MpdrDuplicateCptExamples` and `InpatientCalculationExamples` so the look is
 * familiar across all ruleset edit pages.
 *
 * Two behaviors are documented because a package row can do both at once:
 *
 *   1. Classic packaging — for codes that appear in the BUNDLED list only.
 *      When any trigger CPT is also on the order, those bundled-only codes are
 *      zeroed (100% discount, "Packaged Code").
 *
 *   2. Keep-one consolidation (CLE-2300) — for codes that appear in BOTH the
 *      trigger and bundled lists. Among all of those same-package codes on the
 *      order, keep the single highest-priced line at 100% and zero the rest as
 *      "Packaged Code". This is what implements EAPG same-group consolidation
 *      (e.g. 63047 + 63048 in `Trigger 221` consolidate to one EAPG-221
 *      payment instead of MPDR rank-2 50% on the second line).
 *
 * A small "Currently configured" footer reads the live trigger/bundled lists
 * out of the surrounding form and reports how many codes overlap.
 */

const Wrapper = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  margin: 0 0 0.25rem 0;
`;

const Intro = styled.p`
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const ExampleCard = styled.div`
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.875rem 1rem;
  margin-top: 0.75rem;
`;

const StateLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const Description = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: #495057;
  margin-bottom: 0.5rem;
`;

const FormulaLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #495057;
`;

const ResultLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #2e7d32;
  font-weight: 600;
  margin-top: 0.25rem;
`;

const Footnote = styled.div`
  font-size: 0.8rem;
  color: #856404;
  margin-top: 0.5rem;
  font-style: italic;
`;

const ConfigFooter = styled.div`
  font-size: 0.85rem;
  color: #495057;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #ced4da;
`;

const OverlapBadge = styled.span<{ $active: boolean }>`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-family: monospace;
  font-weight: 600;
  background: ${({ $active }) => ($active ? "#d4edda" : "#e9ecef")};
  color: ${({ $active }) => ($active ? "#155724" : "#495057")};
  margin-left: 0.25rem;
`;

/**
 * Counts how many CPT codes appear in both lists. Empty / null arrays are
 * tolerated so this is safe to call during initial form mount before the user
 * has typed anything.
 */
function countOverlap(
  triggers: string[] | undefined | null,
  bundled: string[] | undefined | null
): number {
  if (!triggers?.length || !bundled?.length) {
    return 0;
  }
  const bundledSet = new Set(bundled);
  return triggers.filter((t) => bundledSet.has(t)).length;
}

export function PackageCalculationExamples() {
  const { watch } = useFormContext<any>();
  const triggers: string[] | undefined = watch("triggerCptCodes");
  const bundled: string[] | undefined = watch("bundledCptCodes");
  const overlap = countOverlap(triggers, bundled);
  const hasKeepOne = overlap > 0;

  return (
    <Wrapper className="block">
      <Title>How this rule works</Title>
      <Intro>
        A package rule fires whenever any of its <strong>trigger</strong> CPT
        codes is on the order. What happens to the package&apos;s{" "}
        <strong>bundled</strong> codes depends on whether they also appear in
        the trigger list:
      </Intro>

      <ExampleCard>
        <StateLabel>1 · Classic packaging (bundled-only codes)</StateLabel>
        <Description>
          When a code is in the <strong>bundled list only</strong>, it pays{" "}
          <strong>$0</strong> any time a trigger is also on the estimate — the
          ancillary procedure is considered part of the primary surgery&apos;s
          payment.
        </Description>
        <FormulaLine>
          Order: trigger CPT @ $16,500 + bundled-only CPT @ $300
        </FormulaLine>
        <FormulaLine>
          Trigger pays 100% = $16,500 · bundled-only @ 100% discount = $0
        </FormulaLine>
        <ResultLine>Total expected = $16,500</ResultLine>
      </ExampleCard>

      <ExampleCard>
        <StateLabel>
          2 · Keep-one consolidation (codes in both lists)
        </StateLabel>
        <Description>
          When a code is in <strong>both</strong> the trigger and bundled lists,
          the package becomes <em>pay-one-per-package</em>: among all of the
          same-package codes on the estimate, the single highest-priced line is
          kept at 100% and every other line is zeroed (100% discount,{" "}
          <code>Packaged Code</code>). This is how EAPG same-group consolidation
          is modeled — for example <code>Trigger 221</code> contains all the
          EAPG-221 laminectomy CPTs in both lists, so two same-EAPG lines pay as
          one.
        </Description>
        <FormulaLine>
          Order: 63047 @ $8,504.41 + 63048 @ $8,504.41 (both EAPG-221)
        </FormulaLine>
        <FormulaLine>
          Highest-priced kept @ 100% = $8,504.41 · other @ 100% discount = $0
        </FormulaLine>
        <ResultLine>Total expected = $8,504.41 (one EAPG payment)</ResultLine>
        <Footnote>
          Without keep-one, the second same-EAPG line would fall through to MPDR
          rank-2 (typically 50%), giving $8,504.41 + $4,252.21 = $12,756.62 — an
          over-expectation against payers that actually consolidate at the EAPG
          level.
        </Footnote>
      </ExampleCard>

      <ConfigFooter>
        Currently configured: this package has{" "}
        <OverlapBadge $active={hasKeepOne}>
          {overlap.toLocaleString()}
        </OverlapBadge>{" "}
        {overlap === 1 ? "code" : "codes"} in <strong>both</strong> the trigger
        and bundled lists
        {hasKeepOne
          ? " → keep-one consolidation is active for those codes."
          : " → keep-one consolidation is not active; only classic packaging applies."}
      </ConfigFooter>
    </Wrapper>
  );
}
